/**
 * DP / Folha — Fase 2.2: Saúde Ocupacional (ASO + EPI)
 * Aba da página DPFolhaConfigPage
 *
 * Estrutura interna:
 *   - Dashboard de Alertas (vencimentos + termos pendentes)
 *   - ASO (Atestados de Saúde Ocupacional): listagem + CRUD + upload PDF + assinatura
 *   - EPI Catálogo: itens disponíveis com CA + validade
 *   - EPI Entregas: fichas com itens + termo + assinatura
 */

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import Autocomplete from '../ui/autocomplete';
import {
  HeartPulse, ShieldCheck, AlertTriangle, FileSignature, Upload, Plus, Pencil, Trash2,
  Download, CheckCircle2, Clock, RefreshCw, Loader2, Stethoscope, HardHat, FileText, ExternalLink
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const fmtDate = (s) => s ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : '—';

const TIPOS_ASO = [
  { v: 'admissional', l: 'Admissional' },
  { v: 'periodico', l: 'Periódico' },
  { v: 'demissional', l: 'Demissional' },
  { v: 'mudanca_funcao', l: 'Mudança de Função' },
  { v: 'retorno_trabalho', l: 'Retorno ao Trabalho' },
];

const CATEGORIAS_EPI = [
  { v: 'capacete', l: 'Capacete' },
  { v: 'oculos', l: 'Óculos / Proteção facial' },
  { v: 'luva', l: 'Luvas' },
  { v: 'calcado', l: 'Calçado de Segurança' },
  { v: 'mascara', l: 'Máscara / Respirador' },
  { v: 'protetor_auricular', l: 'Protetor Auricular' },
  { v: 'cinto', l: 'Cinto / EPI altura' },
  { v: 'avental', l: 'Avental / Vestimenta' },
  { v: 'outros', l: 'Outros' },
];

const MOTIVOS_EPI = [
  { v: 'admissao', l: 'Admissão' },
  { v: 'troca', l: 'Troca por desgaste' },
  { v: 'danificacao', l: 'Danificação' },
  { v: 'vencimento_ca', l: 'Vencimento do CA' },
  { v: 'retorno_substituicao', l: 'Retorno/Substituição' },
];

// ============================================================================
// SUB-TAB: ALERTAS
// ============================================================================
const AlertasTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/calculos-dp/saude/alertas?janela_dias=30`, authHeader());
      setData(r.data);
    } catch (e) {
      toast.error('Erro ao carregar alertas');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="p-6 text-center text-slate-500"><Loader2 className="h-5 w-5 inline animate-spin mr-2" />Carregando alertas...</div>;
  if (!data) return null;

  const card = (title, value, color, Icon) => (
    <Card data-testid={`saude-alerta-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`h-10 w-10 rounded-lg ${color} flex items-center justify-center`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wide">{title}</div>
          <div className="text-2xl font-bold text-slate-800">{value}</div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {card('ASOs Vencidos', data.totais.asos_vencidos, 'bg-red-600', AlertTriangle)}
        {card('ASOs Vencendo (30d)', data.totais.asos_vencendo, 'bg-amber-500', Clock)}
        {card('CA EPI Vencidos', data.totais.epi_ca_vencidos, 'bg-red-600', AlertTriangle)}
        {card('CA EPI Vencendo', data.totais.epi_ca_vencendo, 'bg-amber-500', Clock)}
        {card('Termos Pendentes', data.totais.epi_termos_pendentes, 'bg-indigo-500', FileSignature)}
      </div>

      {data.asos.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Stethoscope className="h-4 w-4 text-red-600" />ASOs com vencimento em até 30 dias</CardTitle></CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm">
              {data.asos.map((a) => (
                <li key={a.id} className="flex items-center justify-between py-1 border-b last:border-0">
                  <span>{a.employee_nome} — {TIPOS_ASO.find(t => t.v === a.tipo)?.l}</span>
                  <Badge variant={a.status === 'vencido' ? 'destructive' : 'default'} className={a.status === 'vencendo' ? 'bg-amber-500' : ''}>
                    {a.status === 'vencido' ? `Vencido há ${Math.abs(a.dias)}d` : `Vence em ${a.dias}d`}
                  </Badge>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {data.epi_itens_ca.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><HardHat className="h-4 w-4 text-amber-600" />EPIs com CA vencendo</CardTitle></CardHeader>
          <CardContent>
            <ul className="space-y-1 text-sm">
              {data.epi_itens_ca.map((e) => (
                <li key={e.id} className="flex items-center justify-between py-1 border-b last:border-0">
                  <span>{e.codigo} — {e.nome} <span className="text-slate-500 text-xs">(CA {e.certificado_aprovacao})</span></span>
                  <Badge variant={e.status === 'vencido' ? 'destructive' : 'default'} className={e.status === 'vencendo' ? 'bg-amber-500' : ''}>
                    {e.status === 'vencido' ? `Vencido há ${Math.abs(e.dias)}d` : `Vence em ${e.dias}d`}
                  </Badge>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {data.totais.asos_vencidos === 0 && data.totais.asos_vencendo === 0 && data.totais.epi_ca_vencidos === 0 && data.totais.epi_ca_vencendo === 0 && data.totais.epi_termos_pendentes === 0 && (
        <Card className="border-emerald-300 bg-emerald-50/30">
          <CardContent className="p-6 text-center">
            <CheckCircle2 className="h-8 w-8 mx-auto text-emerald-600 mb-2" />
            <p className="text-emerald-700 font-medium">Tudo em dia! Sem alertas de Saúde Ocupacional nos próximos 30 dias.</p>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end">
        <Button variant="outline" onClick={load}><RefreshCw className="h-4 w-4 mr-2" /> Atualizar</Button>
      </div>
    </div>
  );
};

// ============================================================================
// SUB-TAB: ASO (CRUD + Upload + Assinatura)
// ============================================================================
const ASOTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const emptyForm = {
    employee_id: '', tipo: 'periodico', data_exame: '', data_emissao: '', data_validade: '',
    resultado: 'apto', restricoes: '', riscos_ocupacionais: [],
    medico_nome: '', medico_crm: '', medico_uf: '', medico_cnes: '', observacoes: '',
  };
  const [form, setForm] = useState(emptyForm);
  const [uploadFile, setUploadFile] = useState({}); // {asoId: File}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rA = await axios.get(`${API}/api/calculos-dp/aso`, authHeader());
      setItems(rA.data || []);
    } catch (e) { toast.error('Erro ao carregar ASOs'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEdit(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (a) => {
    setEdit(a.id);
    setForm({
      ...emptyForm,
      ...a,
      restricoes: a.restricoes || '',
      medico_cnes: a.medico_cnes || '',
      observacoes: a.observacoes || '',
      riscos_ocupacionais: a.riscos_ocupacionais || [],
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.employee_id || !form.data_exame || !form.data_validade || !form.medico_nome || !form.medico_crm) {
      return toast.error('Colaborador, datas e dados do médico são obrigatórios');
    }
    try {
      if (edit) {
        await axios.put(`${API}/api/calculos-dp/aso/${edit}`, form, authHeader());
        toast.success('ASO atualizado');
      } else {
        await axios.post(`${API}/api/calculos-dp/aso`, form, authHeader());
        toast.success('ASO criado');
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao salvar'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Excluir este ASO?')) return;
    try {
      await axios.delete(`${API}/api/calculos-dp/aso/${id}`, authHeader());
      toast.success('ASO excluído'); load();
    } catch (e) { toast.error('Erro ao excluir'); }
  };

  const uploadPdf = async (asoId) => {
    const file = uploadFile[asoId];
    if (!file) return toast.error('Selecione um PDF primeiro');
    try {
      const fd = new FormData();
      fd.append('arquivo', file);
      await axios.post(`${API}/api/calculos-dp/aso/${asoId}/upload-pdf`, fd, {
        ...authHeader(),
        headers: { ...authHeader().headers, 'Content-Type': 'multipart/form-data' },
      });
      toast.success('PDF anexado');
      setUploadFile({ ...uploadFile, [asoId]: null });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro no upload'); }
  };

  const downloadPdf = (asoId) => {
    const token = localStorage.getItem('token');
    fetch(`${API}/api/calculos-dp/aso/${asoId}/pdf`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(b => {
        const url = URL.createObjectURL(b);
        window.open(url, '_blank');
      })
      .catch(() => toast.error('Erro ao baixar PDF'));
  };

  const requestSig = async (asoId) => {
    try {
      await axios.post(`${API}/api/calculos-dp/aso/${asoId}/request-signature`, {}, authHeader());
      toast.success('Assinatura solicitada');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao solicitar'); }
  };

  const markSigned = async (asoId) => {
    try {
      const fd = new FormData();
      fd.append('observacao', 'Assinatura confirmada manualmente');
      await axios.post(`${API}/api/calculos-dp/aso/${asoId}/mark-signed`, fd, authHeader());
      toast.success('Marcado como assinado');
      load();
    } catch (e) { toast.error('Erro'); }
  };

  const sigBadge = (s) => {
    if (s === 'signed') return <Badge className="bg-emerald-500 gap-1"><FileSignature className="h-3 w-3" />Assinado</Badge>;
    if (s === 'pending') return <Badge className="bg-amber-500 gap-1"><Clock className="h-3 w-3" />Pendente</Badge>;
    return <Badge variant="outline">Sem assinatura</Badge>;
  };

  const resBadge = (r) => {
    if (r === 'apto') return <Badge className="bg-emerald-500">Apto</Badge>;
    if (r === 'apto_com_restricao') return <Badge className="bg-amber-500">Apto c/ restrição</Badge>;
    return <Badge variant="destructive">Inapto</Badge>;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-600">{items.length} ASO(s) cadastrado(s).</p>
        <Button onClick={openCreate} data-testid="dp-aso-novo-btn"><Plus className="h-4 w-4 mr-2" />Novo ASO</Button>
      </div>

      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="text-left p-3">Colaborador</th>
              <th className="text-left p-3">Tipo</th>
              <th className="text-left p-3">Exame</th>
              <th className="text-left p-3">Validade</th>
              <th className="text-left p-3">Resultado</th>
              <th className="text-left p-3">PDF</th>
              <th className="text-left p-3">Assinatura</th>
              <th className="text-right p-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="p-6 text-slate-500 text-center"><Loader2 className="h-4 w-4 inline animate-spin mr-2" />Carregando...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={8} className="p-6 text-slate-500 text-center">Nenhum ASO cadastrado.</td></tr>
            ) : items.map((a) => (
              <tr key={a.id} className="border-t hover:bg-slate-50">
                <td className="p-3"><div className="font-medium">{a.employee_nome}</div><div className="text-xs text-slate-500">{a.employee_cargo}</div></td>
                <td className="p-3"><Badge variant="outline">{TIPOS_ASO.find(t => t.v === a.tipo)?.l || a.tipo}</Badge></td>
                <td className="p-3 text-xs">{fmtDate(a.data_exame)}</td>
                <td className="p-3 text-xs">
                  {fmtDate(a.data_validade)}
                  {a.dias_para_vencer !== null && (
                    <div className="text-xs">
                      {a.dias_para_vencer < 0 ? <span className="text-red-600">Vencido</span>
                        : a.dias_para_vencer <= 30 ? <span className="text-amber-600">{a.dias_para_vencer}d</span>
                          : <span className="text-emerald-600">{a.dias_para_vencer}d</span>}
                    </div>
                  )}
                </td>
                <td className="p-3">{resBadge(a.resultado)}</td>
                <td className="p-3">
                  {a.tem_pdf ? (
                    <Button size="sm" variant="ghost" onClick={() => downloadPdf(a.id)} data-testid={`dp-aso-pdf-${a.id}`}>
                      <Download className="h-4 w-4 mr-1" /> Ver PDF
                    </Button>
                  ) : (
                    <div className="flex gap-1 items-center">
                      <Input type="file" accept=".pdf" className="h-8 text-xs w-32"
                        onChange={(e) => setUploadFile({ ...uploadFile, [a.id]: e.target.files?.[0] || null })}
                        data-testid={`dp-aso-file-${a.id}`} />
                      <Button size="sm" variant="ghost" onClick={() => uploadPdf(a.id)} disabled={!uploadFile[a.id]}>
                        <Upload className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </td>
                <td className="p-3">
                  {sigBadge(a.assinatura_status)}
                  {a.tem_pdf && a.assinatura_status === 'none' && (
                    <Button size="sm" variant="ghost" className="ml-1" onClick={() => requestSig(a.id)} data-testid={`dp-aso-sign-req-${a.id}`}>
                      <FileSignature className="h-3 w-3" />
                    </Button>
                  )}
                  {a.assinatura_status === 'pending' && (
                    <Button size="sm" variant="ghost" className="ml-1 text-emerald-700" onClick={() => markSigned(a.id)}>
                      ✓
                    </Button>
                  )}
                </td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(a)}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(a.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="dp-aso-dialog">
          <DialogHeader>
            <DialogTitle>{edit ? 'Editar ASO' : 'Novo ASO'}</DialogTitle>
            <DialogDescription>Dados do atestado de saúde ocupacional. Após salvar, faça upload do PDF e (opcional) solicite assinatura.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Colaborador *</Label>
              <Autocomplete
                value={form.employee_id}
                onValueChange={(v) => setForm({ ...form, employee_id: v })}
                endpoint={`${API}/api/employees-paged`}
                searchParam="search"
                getOptionLabel={(item) => `${item.nome}${item.cpf ? ` · ${item.cpf}` : ''}${item.cargo ? ` · ${item.cargo}` : ''}`}
                getOptionValue={(item) => item.id}
                placeholder="Digite o nome, CPF ou cargo..."
                extraParams={{ active_only: true }}
                emptyMessage="Nenhum funcionário encontrado"
              />
            </div>
            <div>
              <Label>Tipo de ASO *</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TIPOS_ASO.map((t) => <SelectItem key={t.v} value={t.v}>{t.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Resultado *</Label>
              <Select value={form.resultado} onValueChange={(v) => setForm({ ...form, resultado: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="apto">Apto</SelectItem>
                  <SelectItem value="apto_com_restricao">Apto com restrição</SelectItem>
                  <SelectItem value="inapto">Inapto</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Data do Exame *</Label>
              <Input type="date" value={form.data_exame} onChange={(e) => setForm({ ...form, data_exame: e.target.value })} />
            </div>
            <div>
              <Label>Data de Emissão</Label>
              <Input type="date" value={form.data_emissao} onChange={(e) => setForm({ ...form, data_emissao: e.target.value })} />
            </div>
            <div className="col-span-2">
              <Label>Data de Validade *</Label>
              <Input type="date" value={form.data_validade} onChange={(e) => setForm({ ...form, data_validade: e.target.value })} />
            </div>
            <div>
              <Label>Médico — Nome *</Label>
              <Input value={form.medico_nome} onChange={(e) => setForm({ ...form, medico_nome: e.target.value })} />
            </div>
            <div>
              <Label>CRM *</Label>
              <Input type="number" value={form.medico_crm} onChange={(e) => setForm({ ...form, medico_crm: e.target.value })} />
            </div>
            <div>
              <Label>UF do CRM *</Label>
              <Input type="text" value={form.medico_uf} maxLength={2} onChange={(e) => setForm({ ...form, medico_uf: e.target.value.toUpperCase() })} />
            </div>
            <div>
              <Label>CNES da Clínica</Label>
              <Input value={form.medico_cnes} onChange={(e) => setForm({ ...form, medico_cnes: e.target.value })} />
            </div>
            {form.resultado === 'apto_com_restricao' && (
              <div className="col-span-2">
                <Label>Restrições</Label>
                <Textarea value={form.restricoes} onChange={(e) => setForm({ ...form, restricoes: e.target.value })} placeholder="Ex.: Não realizar esforço físico intenso por 60 dias..." />
              </div>
            )}
            <div className="col-span-2">
              <Label>Observações</Label>
              <Textarea value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="dp-aso-salvar-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================================
// SUB-TAB: EPI CATÁLOGO
// ============================================================================
const EPICatalogoTab = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const empty = { codigo: '', nome: '', descricao: '', categoria: 'capacete', certificado_aprovacao: '', validade_ca: '', fabricante: '', modelo: '', estoque_atual: 0, ativo: true };
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/calculos-dp/epi-itens`, authHeader());
      setItems(r.data || []);
    } catch (e) { toast.error('Erro ao listar EPIs'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.codigo || !form.nome || !form.certificado_aprovacao) return toast.error('Código, Nome e CA obrigatórios');
    try {
      if (edit) {
        await axios.put(`${API}/api/calculos-dp/epi-itens/${edit}`, form, authHeader());
        toast.success('Item atualizado');
      } else {
        await axios.post(`${API}/api/calculos-dp/epi-itens`, form, authHeader());
        toast.success('Item criado');
      }
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };
  const remove = async (id) => {
    if (!window.confirm('Excluir este item EPI?')) return;
    try {
      await axios.delete(`${API}/api/calculos-dp/epi-itens/${id}`, authHeader());
      toast.success('Excluído'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-600">{items.length} item(s) cadastrado(s).</p>
        <Button onClick={() => { setEdit(null); setForm(empty); setOpen(true); }} data-testid="dp-epi-novo-btn">
          <Plus className="h-4 w-4 mr-2" /> Novo Item EPI
        </Button>
      </div>
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="text-left p-3">Código</th>
              <th className="text-left p-3">Nome / Categoria</th>
              <th className="text-left p-3">CA</th>
              <th className="text-left p-3">Validade CA</th>
              <th className="text-right p-3">Estoque</th>
              <th className="text-center p-3">Ativo</th>
              <th className="text-right p-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={7} className="p-4 text-slate-500 text-center"><Loader2 className="h-4 w-4 inline animate-spin mr-2" /></td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={7} className="p-4 text-slate-500 text-center">Nenhum item EPI cadastrado.</td></tr>
            ) : items.map((it) => (
              <tr key={it.id} className="border-t hover:bg-slate-50">
                <td className="p-3 font-mono">{it.codigo}</td>
                <td className="p-3">
                  <div className="font-medium">{it.nome}</div>
                  <div className="text-xs text-slate-500">{CATEGORIAS_EPI.find(c => c.v === it.categoria)?.l}</div>
                </td>
                <td className="p-3 font-mono text-xs">{it.certificado_aprovacao}</td>
                <td className="p-3 text-xs">
                  {fmtDate(it.validade_ca)}
                  {it.dias_ca_para_vencer !== null && it.dias_ca_para_vencer !== undefined && (
                    <div className={it.dias_ca_para_vencer < 0 ? 'text-red-600' : it.dias_ca_para_vencer <= 30 ? 'text-amber-600' : 'text-emerald-600'}>
                      {it.dias_ca_para_vencer < 0 ? `Vencido há ${Math.abs(it.dias_ca_para_vencer)}d` : `${it.dias_ca_para_vencer}d`}
                    </div>
                  )}
                </td>
                <td className="p-3 text-right">{it.estoque_atual}</td>
                <td className="p-3 text-center">{it.ativo ? <CheckCircle2 className="h-4 w-4 inline text-emerald-600" /> : <span className="text-slate-400">—</span>}</td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => { setEdit(it.id); setForm({ ...empty, ...it }); setOpen(true); }}><Pencil className="h-4 w-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(it.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="dp-epi-dialog">
          <DialogHeader>
            <DialogTitle>{edit ? 'Editar Item EPI' : 'Novo Item EPI'}</DialogTitle>
            <DialogDescription>Catálogo de EPIs disponíveis para entrega aos colaboradores.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Código *</Label>
              <Input type="number" value={form.codigo} onChange={(e) => setForm({ ...form, codigo: e.target.value })} data-testid="dp-epi-codigo" />
            </div>
            <div>
              <Label>Categoria *</Label>
              <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIAS_EPI.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Nome *</Label>
              <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="dp-epi-nome" />
            </div>
            <div>
              <Label>CA (Certificado de Aprovação) *</Label>
              <Input value={form.certificado_aprovacao} onChange={(e) => setForm({ ...form, certificado_aprovacao: e.target.value })} placeholder="Ex.: 12345" />
            </div>
            <div>
              <Label>Validade do CA</Label>
              <Input type="date" value={form.validade_ca} onChange={(e) => setForm({ ...form, validade_ca: e.target.value })} />
            </div>
            <div>
              <Label>Fabricante</Label>
              <Input value={form.fabricante} onChange={(e) => setForm({ ...form, fabricante: e.target.value })} />
            </div>
            <div>
              <Label>Modelo</Label>
              <Input value={form.modelo} onChange={(e) => setForm({ ...form, modelo: e.target.value })} />
            </div>
            <div>
              <Label>Estoque</Label>
              <Input type="number" min={0} value={form.estoque_atual} onChange={(e) => setForm({ ...form, estoque_atual: parseInt(e.target.value) || 0 })} />
            </div>
            <div className="flex items-end gap-2">
              <Switch checked={form.ativo} onCheckedChange={(v) => setForm({ ...form, ativo: v })} /> <Label>Ativo</Label>
            </div>
            <div className="col-span-2">
              <Label>Descrição</Label>
              <Textarea value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="dp-epi-salvar-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================================
// SUB-TAB: EPI ENTREGAS
// ============================================================================
const EPIEntregasTab = () => {
  const [entregas, setEntregas] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const empty = {
    employee_id: '', data_entrega: new Date().toISOString().slice(0, 10),
    motivo: 'admissao', itens: [], observacoes: '',
  };
  const [form, setForm] = useState(empty);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rE, rC] = await Promise.all([
        axios.get(`${API}/api/calculos-dp/epi-entregas`, authHeader()),
        axios.get(`${API}/api/calculos-dp/epi-itens?ativo=true`, authHeader()),
      ]);
      setEntregas(rE.data || []);
      setCatalogo(rC.data || []);
    } catch (e) { toast.error('Erro ao carregar entregas'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(empty); setOpen(true); };

  const save = async () => {
    if (!form.employee_id || form.itens.length === 0) {
      return toast.error('Colaborador e ao menos 1 item são obrigatórios');
    }
    try {
      await axios.post(`${API}/api/calculos-dp/epi-entregas`, form, authHeader());
      toast.success('Entrega registrada');
      setOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Excluir esta entrega?')) return;
    try {
      await axios.delete(`${API}/api/calculos-dp/epi-entregas/${id}`, authHeader());
      toast.success('Excluído'); load();
    } catch (e) { toast.error('Erro'); }
  };

  const requestSig = async (id) => {
    try {
      await axios.post(`${API}/api/calculos-dp/epi-entregas/${id}/request-signature`, {}, authHeader());
      toast.success('Termo enviado para assinatura'); load();
    } catch (e) { toast.error('Erro'); }
  };

  const markSigned = async (id) => {
    try {
      await axios.post(`${API}/api/calculos-dp/epi-entregas/${id}/mark-signed`, {}, authHeader());
      toast.success('Termo marcado como assinado'); load();
    } catch (e) { toast.error('Erro'); }
  };

  const addItem = () => setForm({ ...form, itens: [...form.itens, { epi_item_id: '', quantidade: 1 }] });
  const updItem = (i, k, v) => {
    const arr = [...form.itens];
    arr[i] = { ...arr[i], [k]: v };
    setForm({ ...form, itens: arr });
  };
  const delItem = (i) => setForm({ ...form, itens: form.itens.filter((_, idx) => idx !== i) });

  const sigBadge = (s) => {
    if (s === 'signed') return <Badge className="bg-emerald-500 gap-1"><FileSignature className="h-3 w-3" />Assinado</Badge>;
    if (s === 'pending') return <Badge className="bg-amber-500 gap-1"><Clock className="h-3 w-3" />Pendente</Badge>;
    return <Badge variant="outline">Sem termo</Badge>;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-600">{entregas.length} entrega(s) registrada(s).</p>
        <Button onClick={openCreate} disabled={catalogo.length === 0} data-testid="dp-epi-entrega-novo-btn">
          <Plus className="h-4 w-4 mr-2" /> Nova Entrega
        </Button>
      </div>
      {catalogo.length === 0 && (
        <Card className="border-amber-300"><CardContent className="p-3 text-sm text-amber-700 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" /> Cadastre pelo menos um item EPI no catálogo antes de registrar entregas.
        </CardContent></Card>
      )}
      <div className="overflow-x-auto rounded-md border">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-600">
            <tr>
              <th className="text-left p-3">Colaborador</th>
              <th className="text-left p-3">Data</th>
              <th className="text-left p-3">Motivo</th>
              <th className="text-left p-3">Itens</th>
              <th className="text-left p-3">Termo</th>
              <th className="text-right p-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="p-4 text-slate-500 text-center"><Loader2 className="h-4 w-4 inline animate-spin mr-2" /></td></tr>
            ) : entregas.length === 0 ? (
              <tr><td colSpan={6} className="p-4 text-slate-500 text-center">Nenhuma entrega registrada.</td></tr>
            ) : entregas.map((e) => (
              <tr key={e.id} className="border-t hover:bg-slate-50">
                <td className="p-3"><div className="font-medium">{e.employee_nome}</div><div className="text-xs text-slate-500">{e.employee_cargo}</div></td>
                <td className="p-3 text-xs">{fmtDate(e.data_entrega)}</td>
                <td className="p-3"><Badge variant="outline">{MOTIVOS_EPI.find(m => m.v === e.motivo)?.l || e.motivo}</Badge></td>
                <td className="p-3 text-xs">
                  {e.itens.map((sub, i) => (
                    <div key={i}>{sub.codigo} {sub.nome} ×{sub.quantidade} <span className="text-slate-400">(CA {sub.ca_no_momento || '—'})</span></div>
                  ))}
                </td>
                <td className="p-3">
                  {sigBadge(e.termo_assinatura_status)}
                  {e.termo_assinatura_status === 'none' && (
                    <Button size="sm" variant="ghost" className="ml-1" onClick={() => requestSig(e.id)} data-testid={`dp-epi-entrega-sign-${e.id}`}>
                      <FileSignature className="h-3 w-3" />
                    </Button>
                  )}
                  {e.termo_assinatura_status === 'pending' && (
                    <Button size="sm" variant="ghost" className="ml-1 text-emerald-700" onClick={() => markSigned(e.id)}>✓</Button>
                  )}
                </td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="ghost" onClick={() => remove(e.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="dp-epi-entrega-dialog">
          <DialogHeader>
            <DialogTitle>Nova Entrega de EPI</DialogTitle>
            <DialogDescription>Registro de itens entregues ao colaborador. CA e validade são copiados do catálogo no momento da entrega.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Colaborador *</Label>
              <Autocomplete
                value={form.employee_id}
                onValueChange={(v) => setForm({ ...form, employee_id: v })}
                endpoint={`${API}/api/employees-paged`}
                searchParam="search"
                getOptionLabel={(item) => `${item.nome}${item.cpf ? ` · ${item.cpf}` : ''}${item.cargo ? ` · ${item.cargo}` : ''}`}
                getOptionValue={(item) => item.id}
                placeholder="Digite o nome, CPF ou cargo..."
                extraParams={{ active_only: true }}
                emptyMessage="Nenhum funcionário encontrado"
              />
            </div>
            <div>
              <Label>Data da Entrega *</Label>
              <Input type="date" value={form.data_entrega} onChange={(e) => setForm({ ...form, data_entrega: e.target.value })} />
            </div>
            <div>
              <Label>Motivo *</Label>
              <Select value={form.motivo} onValueChange={(v) => setForm({ ...form, motivo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{MOTIVOS_EPI.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2 mt-2">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base">Itens entregues *</Label>
                <Button type="button" size="sm" variant="outline" onClick={addItem} data-testid="dp-epi-entrega-add-item">
                  <Plus className="h-3 w-3 mr-1" /> Adicionar
                </Button>
              </div>
              {form.itens.length === 0 ? (
                <p className="text-xs text-slate-500 italic">Adicione ao menos 1 item.</p>
              ) : form.itens.map((sub, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-end p-2 border rounded bg-slate-50 mb-1">
                  <div className="col-span-8">
                    <Label className="text-xs">EPI</Label>
                    <Select value={sub.epi_item_id} onValueChange={(v) => updItem(i, 'epi_item_id', v)}>
                      <SelectTrigger className="h-9"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                      <SelectContent>{catalogo.map((c) => <SelectItem key={c.id} value={c.id}>{c.codigo} — {c.nome} (CA {c.certificado_aprovacao})</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-3">
                    <Label className="text-xs">Qtd</Label>
                    <Input type="number" min={1} className="h-9" value={sub.quantidade} onChange={(e) => updItem(i, 'quantidade', parseInt(e.target.value) || 1)} />
                  </div>
                  <div className="col-span-1">
                    <Button type="button" size="sm" variant="ghost" onClick={() => delItem(i)}>
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="col-span-2">
              <Label>Observações</Label>
              <Textarea value={form.observacoes} onChange={(e) => setForm({ ...form, observacoes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={save} data-testid="dp-epi-entrega-salvar-btn">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================
const SaudeOcupacionalTab = () => {
  return (
    <Tabs defaultValue="alertas" className="w-full">
      <TabsList className="grid grid-cols-4 mb-4">
        <TabsTrigger value="alertas" data-testid="dp-saude-tab-alertas"><AlertTriangle className="h-4 w-4 mr-1" />Alertas</TabsTrigger>
        <TabsTrigger value="aso" data-testid="dp-saude-tab-aso"><Stethoscope className="h-4 w-4 mr-1" />ASO</TabsTrigger>
        <TabsTrigger value="epi-catalogo" data-testid="dp-saude-tab-epi-cat"><HardHat className="h-4 w-4 mr-1" />Catálogo EPI</TabsTrigger>
        <TabsTrigger value="epi-entregas" data-testid="dp-saude-tab-epi-entregas"><FileText className="h-4 w-4 mr-1" />Entregas EPI</TabsTrigger>
      </TabsList>
      <TabsContent value="alertas"><AlertasTab /></TabsContent>
      <TabsContent value="aso"><ASOTab /></TabsContent>
      <TabsContent value="epi-catalogo"><EPICatalogoTab /></TabsContent>
      <TabsContent value="epi-entregas"><EPIEntregasTab /></TabsContent>
    </Tabs>
  );
};

export default SaudeOcupacionalTab;
